import { useT } from '../i18n';

// Pagination built on the dz-shared-styles `.pagination` component family:
//   .pagination                       — the flex row (sans-m, gap, margins)
//   .pagination__number-button        — a page number (gets --active highlight)
//   .pagination__text-button          — the prev/next labels
//   .pagination__ellipses-button      — the non-interactive gap marker
// Each button also carries the shared `.button .button--ghost` base so it
// inherits the pill shape, padding, and typography. Prev/next use the shared
// `data-icon-before` / `data-icon-after` hooks to render Material Symbols
// chevrons. Page numbers are 0-based internally and rendered as 1-based.

const cn = (...parts: Array<string | false | null | undefined>) =>
  parts.filter(Boolean).join(' ');

// Build the list of page slots to render: always the first and last page,
// plus a one-page window around the current page, with 'ellipsis' markers
// bridging any gaps. For small page counts this collapses to "show all".
function getPageItems(current: number, count: number): Array<number | 'ellipsis'> {
  const pages = new Set<number>([0, count - 1]);
  for (let p = current - 1; p <= current + 1; p++) {
    if (p >= 0 && p < count) pages.add(p);
  }
  const sorted = [...pages].sort((a, b) => a - b);
  const items: Array<number | 'ellipsis'> = [];
  let prev = -1;
  for (const p of sorted) {
    if (prev !== -1 && p - prev > 1) items.push('ellipsis');
    items.push(p);
    prev = p;
  }
  return items;
}

interface PaginationProps {
  /** 0-based index of the active page. */
  currentPage: number;
  /** Total number of pages. */
  pageCount: number;
  /** Called with the 0-based target page. */
  onPageChange: (page: number) => void;
}

export function Pagination({ currentPage, pageCount, onPageChange }: PaginationProps) {
  const m = useT();
  if (pageCount <= 1) return null;

  const atStart = currentPage <= 0;
  const atEnd = currentPage >= pageCount - 1;
  const items = getPageItems(currentPage, pageCount);

  return (
    <div className="pagination">
      <div
        className={cn('button', 'button--ghost', 'pagination__text-button', atStart && 'disabled')}
        data-icon-before="chevron_left"
        role="button"
        aria-label={m.pagination.previousAria}
        onClick={() => !atStart && onPageChange(currentPage - 1)}
      >
        {m.pagination.previous}
      </div>

      {items.map((item, i) =>
        item === 'ellipsis' ? (
          <div key={`ellipsis-${i}`} className="button button--ghost pagination__ellipses-button">
            …
          </div>
        ) : (
          <div
            key={item}
            className={cn(
              'button',
              'button--ghost',
              'pagination__number-button',
              item === currentPage && 'pagination__number-button--active',
            )}
            role="button"
            aria-current={item === currentPage ? 'page' : undefined}
            onClick={() => onPageChange(item)}
          >
            {item + 1}
          </div>
        ),
      )}

      <div
        className={cn('button', 'button--ghost', 'pagination__text-button', atEnd && 'disabled')}
        data-icon-after="chevron_right"
        role="button"
        aria-label={m.pagination.nextAria}
        onClick={() => !atEnd && onPageChange(currentPage + 1)}
      >
        {m.pagination.next}
      </div>
    </div>
  );
}
